/**
 * Contextual selection helpers.
 *
 * The BIM-style "Modify | <Category>" ribbon derives everything from the
 * current selection. Selection is written to BOTH stores by the select tool
 * (appStore.selectedIds drives the viewport controller, bimStore.selection
 * drives the kernel highlight), so resolution merges both and preserves the
 * bimStore order as the "dominant" reference category.
 */
import { BIMElement } from '../core/types';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import * as THREE from 'three';

/**
 * Every selected id resolved to its BIM element (unknown ids dropped).
 * Order: bimStore.selection first (primary selection), then extras from
 * appStore. Deduplicated. Grid datums (project.grids) are resolved to a
 * synthetic element wrapper so category-driven tooling sees them as 'Grids'.
 */
export function getSelectedElements(): BIMElement[] {
    const bim = useBIMStore.getState();
    const app = useAppStore.getState();

    const ids: string[] = [];
    const seen = new Set<string>();
    const push = (id: string) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
    };
    bim.selection.forEach(push);
    app.selectedIds.forEach(push);

    const out: BIMElement[] = [];
    for (const id of ids) {
        const el = bim.project.elements.get(id);
        if (el) {
            out.push(el);
            continue;
        }
        const grid = bim.project.grids.get(id);
        if (grid) {
            out.push({
                id: grid.id,
                category: 'Grids',
                type: 'Grid',
                name: grid.name,
                parameters: { 'Name': grid.name },
                constraints: [],
            } as unknown as BIMElement);
            continue;
        }
        const level = bim.project.levels.get(id);
        if (level) {
            out.push({
                id: level.id,
                category: 'Levels',
                type: 'Level',
                name: level.name,
                parameters: { 'Name': level.name, 'Elevation': level.elevation },
                constraints: [],
                geometryData: { elevation: level.elevation },
            } as unknown as BIMElement);
        }
    }
    return out;
}

/** Raw merged selection ids (for tools that only need ids). */
export function getSelectedIds(): string[] {
    const bim = useBIMStore.getState();
    const app = useAppStore.getState();
    const seen = new Set<string>();
    const ids: string[] = [];
    bim.selection.forEach((id) => { if (!seen.has(id)) { seen.add(id); ids.push(id); } });
    app.selectedIds.forEach((id) => { if (!seen.has(id)) { seen.add(id); ids.push(id); } });
    return ids;
}

/**
 * The primary selected element (defines the contextual tab label). Prefers the
 * first bimStore selection entry, which mirrors BIM's "last fully clicked
 * element sets the tab".
 */
export function getPrimarySelectedElement(): BIMElement | null {
    const els = getSelectedElements();
    return els.length > 0 ? els[0] : null;
}

/**
 * Normalize a category / type into the contextual tab label, e.g.
 * 'Walls' -> 'Walls', 'Generic Models' -> 'Walls' is NOT done here; the label
 * is the friendly plural category name.
 */
export function contextualCategoryLabel(element: BIMElement | null): string {
    if (!element) return '';
    if (element.type === 'Imported CAD' || element.category === 'CAD Links') return 'Imported CAD';
    switch (element.category) {
        case 'Model Groups':
        case 'ModelGroups':
            return 'Model Groups';
        case 'Text Notes':
        case 'ModelText':
        case 'Model Text':
            return 'Text';
        case 'Dimensions':
            return 'Dimensions';
        case 'Levels':
            return 'Levels';
        case 'Grids':
            return 'Grids';
        case 'Raster Images':
            return 'Images';
        case 'Toposolid':
            return 'Toposolid';
        default:
            return element.category;
    }
}

/**
 * Rough world-space AABB for an element, derived from its geometryData. Used by
 * Selection Box, Zoom to Selection and extents tools. Returns null when the
 * geometry kind is unrecognized.
 */
export function elementWorldBounds(element: BIMElement): THREE.Box3 | null {
    const g = (element as any).geometryData;
    const box = new THREE.Box3();
    if (!g) return null;

    const addPoint = (p?: number[] | number) => {
        if (Array.isArray(p) && p.length >= 2) {
            box.expandByPoint(new THREE.Vector3(p[0], p[1], p[2] ?? 0));
        }
    };
    const addVec = (v?: THREE.Vector3) => { if (v) box.expandByPoint(v); };

    if (Array.isArray(g.start) && Array.isArray(g.end)) {
        addPoint(g.start);
        addPoint(g.end);
        // Walls/beams have a height axis along Z.
        const height = g.height ?? 0;
        if (Array.isArray(g.start)) {
            box.expandByPoint(new THREE.Vector3(g.start[0], g.start[1], (g.start[2] ?? 0) + height));
            box.expandByPoint(new THREE.Vector3(g.end[0], g.end[1], (g.end[2] ?? 0) + height));
        }
        return box;
    }
    if (Array.isArray(g.boundary) && g.boundary.length) {
        const base = g.baseElevation ?? g.heightOffsetFromLevel ?? 0;
        g.boundary.forEach((p: number[]) => addPoint([p[0], p[1], base]));
        return box;
    }
    if (Array.isArray(g.footprint) && g.footprint.length) {
        g.footprint.forEach((p: number[]) => addPoint([p[0], p[1], g.baseOffsetFromLevel ?? 0]));
        return box;
    }
    if (Array.isArray(g.path) && g.path.length) {
        g.path.forEach((p: number[]) => addPoint(p));
        return box;
    }
    if (Array.isArray(g.origin)) {
        addPoint(g.origin);
    }
    if (Array.isArray(g.position)) {
        const pos = g.position;
        const w = g.width ?? 1, d = g.depth ?? 1, h = g.height ?? 1;
        box.min.set(pos[0] - w / 2, pos[1] - d / 2, pos[2]);
        box.max.set(pos[0] + w / 2, pos[1] + d / 2, pos[2] + h);
    }
    if (Array.isArray(g.points) && g.points.length) {
        g.points.forEach((p: number[]) => addPoint(p));
    }
    if (box.isEmpty()) return null;
    addVec; // keep helper referenced
    return box;
}

/** Union AABB of the current selection, or null. */
export function selectionBounds(): THREE.Box3 | null {
    const merged = new THREE.Box3();
    for (const el of getSelectedElements()) {
        const b = elementWorldBounds(el);
        if (b) merged.union(b);
    }
    return merged.isEmpty() ? null : merged;
}

/** Approximate measurable length for wall/line/beam-style elements (meters). */
export function elementLength(element: BIMElement): number {
    const g = (element as any).geometryData;
    if (!g) return 0;
    if (Array.isArray(g.start) && Array.isArray(g.end)) {
        return new THREE.Vector2(g.start[0], g.start[1]).distanceTo(new THREE.Vector2(g.end[0], g.end[1]));
    }
    if (Array.isArray(g.path) && g.path.length > 1) {
        let sum = 0;
        for (let i = 1; i < g.path.length; i++) {
            sum += new THREE.Vector3(...(g.path[i - 1] as number[])).distanceTo(new THREE.Vector3(...(g.path[i] as number[])));
        }
        return sum;
    }
    if (Array.isArray(g.boundary) && g.boundary.length > 2) {
        let sum = 0;
        const b = g.boundary as number[][];
        for (let i = 1; i < b.length; i++) {
            sum += new THREE.Vector2(b[i - 1][0], b[i - 1][1]).distanceTo(new THREE.Vector2(b[i][0], b[i][1]));
        }
        return sum;
    }
    return 0;
}
